//
// Copyright (c) 2011-2024 by Curalate, Inc.
//

import { UnisonIconType } from '@bvengineering/unison-core';
import React from 'react';
import styled, { css } from 'styled-components';

import { PurchaseOption } from '@apis/campaign-requests/sampling/opportunities/samplingOpportunitiesApiModel';
import { SortDir } from '@apis/campaign-requests/shared/Sorting';
import { usePurchaseOptionsContext } from '@campaigns/components/shared/purchase-options/PurchaseOptionsContext';
import { PurchaseOptionsTableRow } from '@campaigns/components/shared/purchase-options/PurchaseOptionsTableRow';
import {
  PurchaseOptionsSortKey,
  WIDTH_CHECKBOX,
  WIDTH_EXPIRATION_DATE,
  WIDTH_UNITS,
} from '@campaigns/constants/sampling/SamplingCampaignRequestConstants';
import { SAMPLING_CAMPAIGNS_TRACKING_ID } from '@campaigns/constants/sampling/SamplingCampaignsTrackingId';
import { InvisibleButton } from '@components/atoms/button/Button';
import Icon from '@components/atoms/icon/Icon';
import { BodyText, TableHeaderCellText } from '@components/atoms/text/Text';
import VisuallyHiddenText from '@components/atoms/visually-hidden-text/VisuallyHiddenText';
import { Table } from '@components/organisms/table/Table';

const TABLE_CAPTION_ID = 'sampling-campaigns-purchase-options-table-caption';

interface PurchaseOptionsTableProps {
  className?: string;
  onSortChange: (sortKey: PurchaseOptionsSortKey) => void;
  purchaseOptions: PurchaseOption[];
  sortDir: SortDir;
  sortKey: PurchaseOptionsSortKey;
}

export const PurchaseOptionsTable = ({
  className,
  onSortChange,
  purchaseOptions,
  sortDir,
  sortKey,
}: PurchaseOptionsTableProps) => {
  const { readOnly } = usePurchaseOptionsContext();

  if (purchaseOptions.length === 0) {
    return (
      <EmptyText>
        No options matched your search criteria. Please refine your search and
        try again.
      </EmptyText>
    );
  }

  const iconName: UnisonIconType =
    sortDir === SortDir.ASC ? 'chevron-up' : 'chevron-down';

  const headerCells = [
    // Conditionally include checkbox column for editable mode
    ...(!readOnly
      ? [
          <CheckboxCell
            key='selected'
            justifyContent='flex-start'
          ></CheckboxCell>,
        ]
      : []),
    <TableHeaderCellText key='id' justifyContent='flex-start'>
      ID
    </TableHeaderCellText>,
    <TableHeaderCellText key='details' justifyContent='flex-start'>
      PURCHASE OPTION DETAILS
    </TableHeaderCellText>,
    <InvisibleButton
      key='unitsRemaining'
      onClick={() => {
        onSortChange(PurchaseOptionsSortKey.BALANCE);
      }}
      trackingId={
        SAMPLING_CAMPAIGNS_TRACKING_ID.CAMPAIGN_REQUEST_PURCHASE_OPTIONS_SORT_BY_UNITS_REMAINING_BUTTON
      }
    >
      <NumberCell justifyContent='flex-end'>
        UNITS
        <VisuallyHiddenText>
          Sort by units&nbsp;
          {sortDir === SortDir.ASC ? 'ascending' : 'descending'}
        </VisuallyHiddenText>
        {sortKey === PurchaseOptionsSortKey.BALANCE && <Icon name={iconName} />}
      </NumberCell>
    </InvisibleButton>,
    <InvisibleButton
      key='expiration'
      onClick={() => {
        onSortChange(PurchaseOptionsSortKey.EXPIRY_DATE);
      }}
      trackingId={
        SAMPLING_CAMPAIGNS_TRACKING_ID.CAMPAIGN_REQUEST_PURCHASE_OPTIONS_SORT_BY_EXPIRATION_DATE_BUTTON
      }
    >
      <ExpirationDateCell justifyContent='flex-start'>
        EXPIRY
        <VisuallyHiddenText>
          Sort by expiry&nbsp;
          {sortDir === SortDir.ASC ? 'ascending' : 'descending'}
        </VisuallyHiddenText>
        {sortKey === PurchaseOptionsSortKey.EXPIRY_DATE && (
          <Icon name={iconName} />
        )}
      </ExpirationDateCell>
    </InvisibleButton>,
  ];

  return (
    <Table
      body={purchaseOptions.map((purchaseOption) => (
        <PurchaseOptionsTableRow
          key={purchaseOption.opportunityId}
          purchaseOption={purchaseOption}
        />
      ))}
      caption={<VisuallyHiddenText>Purchase Options</VisuallyHiddenText>}
      captionId={TABLE_CAPTION_ID}
      className={className}
      headerCells={headerCells}
    />
  );
};

const StyledTableHeaderCellText = styled(TableHeaderCellText)`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacings[1]};
`;

const CheckboxCell = styled(TableHeaderCellText)`
  ${WIDTH_CHECKBOX}
`;

const NumberCell = styled(StyledTableHeaderCellText)`
  ${({ theme }) => {
    return css`
      ${WIDTH_UNITS({ theme })}
      padding-right: ${theme.spacings[5]};
    `;
  }}
`;

const ExpirationDateCell = styled(StyledTableHeaderCellText)`
  ${WIDTH_EXPIRATION_DATE}
`;

const EmptyText = styled(BodyText)(
  ({ theme }) => `
    margin-top: ${theme.spacings[2]};
  `,
);
